/**
 * Conversations WhatsApp et SMS (docs/CONVERSATIONS.md) – ce que partagent les écrans du socle (fil d'un objet, boîte
 * « À trier », administration des numéros et des modèles) et la maquette :
 *  - forme des réponses de l'API (api/src/conversations/service.ts fait foi). PROVISOIRE : ces types ne sont pas encore
 *    dans packages/shared/src/api-types.ts ; à y déplacer (schémas Zod de réponse) avec la prochaine évolution de l'API ;
 *  - objets qui portent un fil (comme OBJETS_CONVERSATION de l'API) et clés des requêtes.
 */
import type { CanalConversation, ModeleConversation, NumeroCanal, StatutEnvoi } from '@agora/shared';

/** Objets AGORA qui portent un fil (api/src/conversations/objets.ts) : clé = nœud du catalogue qui porte les droits. */
export const OBJETS_FIL = ['it.demandes.incidents', 'commerce.vente.affaires', 'tiers'] as const;
export type ObjetFil = (typeof OBJETS_FIL)[number];

/** Traduction d'un message (W6) : texte dans une langue ; `envoyee` = la traduction relue qui est partie. */
export interface TraductionMessage {
  texte: string;
  envoyee?: boolean;
}

/** Message d'un fil (GET /v1/conversations/fil, GET /v1/conversations/a-trier/{id}). */
export interface MessageFil {
  id: string;
  sens: 'entrant' | 'sortant';
  type: string;
  canal: CanalConversation;
  contenu: string | null;
  langue: string | null;
  traduction: Record<string, TraductionMessage>;
  statut: StatutEnvoi;
  prevuLe: string | null;
  erreur: string | null;
  auteur: string | null;
  automatique: boolean;
  creeLe: string;
  conversationId: string;
}

/** Modèle d'un objet (GET /v1/conversations/modeles) et de l'administration (GET /v1/admin/conversations/modeles). */
export type ModeleEnregistre = ModeleConversation & {
  id: string;
  /** Variables citées par les textes (les mêmes dans toutes les langues). */
  variables: string[];
  /** Statut chez Meta par langue (tenu à jour par la réception). */
  statutsMeta: Record<string, { statut?: string }>;
};

/** Numéro PRODEVAL (GET /v1/admin/conversations/numeros). */
export type NumeroEnregistre = NumeroCanal & { id: string };

/** Préparation d'un message (POST /v1/conversations/preparer) : ce qui partira, ou l'empêchement. */
export type Preparation =
  | {
    possible: true; canal: CanalConversation; fenetre: boolean; finFenetre: string | null; initiative: boolean; prevuLe: string;
    langue: string | null; langueCorrespondant: string | null; contenu: string; modele: string | null;
  }
  | { possible: false; code: string; detail: string; champs?: Record<string, string> };

/** Message mis en file d'envoi (POST /v1/conversations/envoyer, 201). */
export interface MessageEnFile {
  id: string;
  conversationId: string;
  canal: CanalConversation;
  langue: string | null;
  initiative: boolean;
  prevuLe: string;
}

/** Conversation de la boîte « À trier » (GET /v1/conversations/a-trier). */
export interface ConversationATrier {
  id: string;
  filiale: string;
  canal: CanalConversation;
  /** Nom affiché du numéro PRODEVAL qui a reçu le message. */
  numeroPrd: string;
  /** Numéro du correspondant (E.164). */
  correspondant: string;
  collaborateur: string | null;
  contactId: string | null;
  tiersId: string | null;
  langue: string | null;
  dernierEntrantLe: string | null;
  dernierMessage: string | null;
  messages: number;
  statut: string;
}

/** Une conversation « À trier » et ses messages (GET /v1/conversations/a-trier/{id}). */
export interface ConversationDetaillee {
  conversation: ConversationATrier;
  messages: MessageFil[];
}

/** Rattachement fait (POST /v1/conversations/a-trier/{id}/rattachement). */
export interface RattachementFait {
  id: string;
  ressource: string;
  objetId: string;
  messages: number;
}

/** Clés des requêtes (TanStack Query) : fil et modèles d'un objet, boîte « À trier », administration. */
export const CLE_CONVERSATIONS = ['conversations'] as const;
export const cleFil = (ressource: string, objetId: string) => ['conversations', 'fil', ressource, objetId] as const;
export const cleModelesObjet = (ressource: string, objetId: string) => ['conversations', 'modeles', ressource, objetId] as const;
export const cleATrier = (filiale: string) => ['conversations', 'a-trier', filiale] as const;
export const CLE_ADMIN_CONVERSATIONS = ['conversations', 'administration'] as const;

/** Envoi différé (hors horaires, W8) : heure prévue au-delà d'une minute. */
export const envoiDiffere = (prevuLe: string | null | undefined, maintenant = Date.now()): boolean =>
  !!prevuLe && new Date(prevuLe).getTime() > maintenant + 60_000;
